import React, { useState } from "react";
import {
  IconPlus,
  IconReceipt,
  IconTruck,
  IconShieldCheck,
  IconSearch,
  IconPackage,
  IconCheck,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useStoreSettings } from "../../controllers/settings.controller";
import {
  createAcquisition,
  useAssetAcquisitions,
  useAssetCategories
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetAcquisitionsTab({ acquisitions: propAcquisitions, suppliers, categories: propCategories, onRefresh }) {
  const { data: fetchedAcquisitions, mutate: mutateAcquisitions } = useAssetAcquisitions();
  const { data: fetchedCategories } = useAssetCategories();
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs.";

  const acquisitionList = propAcquisitions || fetchedAcquisitions || [];
  const categoryList = propCategories || fetchedCategories || [];

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_id: "",
    invoice_number: "",
    purchase_order_ref: "",
    goods_receipt_ref: "",
    receipt_date: new Date().toISOString().split("T")[0],
    total_cost: 0,
    freight_cost: 0,
    installation_cost: 0,
    inspection_status: "Passed",
    inspection_notes: "",
    capitalization_decision: "Capitalize",
    location: "Main Store",
    department: "Operations",
    generate_assets: true,
    items: [
      { name: "", category_id: "", quantity: 1, unit_cost: 0, serial_numbers: [] },
    ],
  });

  const handleAddItemRow = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        { name: "", category_id: "", quantity: 1, unit_cost: 0, serial_numbers: [] },
      ],
    });
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];
    updated[index][field] = value;
    setForm({ ...form, items: updated });
  };

  const handleRemoveItem = (index) => {
    if (form.items.length <= 1) return;
    const updated = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.receipt_date) return toast.error("Receipt date is required");
    if (form.items.some((it) => !it.name.trim())) return toast.error("All items must have a name");

    try {
      const res = await createAcquisition(form);
      toast.success(res.message || "Acquisition recorded!");
      setModalOpen(false);
      if (mutateAcquisitions) mutateAcquisitions();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to record acquisition");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="bg-white dark:bg-neutral-800 p-4 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800 dark:text-white text-base">Asset Acquisition & Goods Receiving</h3>
          <p className="text-xs text-gray-500">Record equipment deliveries, inspect quality, allocate landed costs, and auto-generate asset tags.</p>
        </div>

        <button
          onClick={() => setModalOpen(true)}
          className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white border-0 flex items-center gap-1"
        >
          <IconPlus size={16} stroke={iconStroke} /> New Acquisition / Receiving
        </button>
      </div>

      {/* Acquisitions Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-neutral-900/60 text-gray-600 dark:text-gray-300">
                <th>Acquisition #</th>
                <th>Supplier</th>
                <th>Invoice / PO Ref</th>
                <th>Receipt Date</th>
                <th>Inspection</th>
                <th>Capitalization Policy</th>
                <th className="text-right">Goods Cost</th>
                <th className="text-right">Freight & Install</th>
                <th className="text-right">Total Landed Cost</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {(!acquisitionList || acquisitionList.length === 0) ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-400">
                    <IconPackage size={36} className="mx-auto mb-2 opacity-30" stroke={iconStroke} />
                    No equipment acquisitions recorded yet.
                  </td>
                </tr>
              ) : (
                acquisitionList.map((acq) => (
                  <tr key={acq.id} className="hover:bg-emerald-50/30 dark:hover:bg-neutral-700/30">
                    <td className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                      {acq.acquisition_number}
                    </td>
                    <td>{acq.supplier_name || "Direct Purchase"}</td>
                    <td className="text-xs font-mono">
                      {[acq.invoice_number ? `Inv: ${acq.invoice_number}` : null, acq.purchase_order_ref ? `PO: ${acq.purchase_order_ref}` : null]
                        .filter(Boolean)
                        .join(" • ") || "—"}
                    </td>
                    <td>{acq.receipt_date ? new Date(acq.receipt_date).toLocaleDateString() : "—"}</td>
                    <td>
                      <span
                        className={`badge badge-sm ${
                          acq.inspection_status === "Passed"
                            ? "badge-success text-white"
                            : acq.inspection_status === "Failed"
                            ? "badge-error text-white"
                            : "badge-warning"
                        }`}
                      >
                        {acq.inspection_status || "Passed"}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-outline badge-sm">
                        {acq.capitalization_decision || "Capitalize"}
                      </span>
                    </td>
                    <td className="text-right font-medium">
                      {currency} {Number(acq.total_cost || 0).toLocaleString()}
                    </td>
                    <td className="text-right text-xs text-gray-500">
                      {currency} {Number((acq.freight_cost || 0) + (acq.installation_cost || 0)).toLocaleString()}
                    </td>
                    <td className="text-right font-bold text-emerald-700 dark:text-emerald-400">
                      {currency}{" "}
                      {Number((acq.total_cost || 0) + (acq.freight_cost || 0) + (acq.installation_cost || 0)).toLocaleString()}
                    </td>
                    <td className="text-center">
                      <span className="badge badge-ghost badge-sm">{acq.status || "Received"}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Acquisition Modal */}
      {modalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">
              Receive Equipment & Ingest Assets
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Allocate costs and optionally auto-generate individual asset register items with unique tags.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Supplier</label>
                  <select
                    value={form.supplier_id}
                    onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="">Select Supplier (Optional)</option>
                    {(suppliers || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Supplier Invoice #</label>
                  <input
                    type="text"
                    placeholder="INV-99281"
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Receipt Date *</label>
                  <input
                    type="date"
                    required
                    value={form.receipt_date}
                    onChange={(e) => setForm({ ...form, receipt_date: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Total Purchase Price ({currency})</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.total_cost}
                    onChange={(e) => setForm({ ...form, total_cost: parseFloat(e.target.value) || 0 })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Freight & Shipping ({currency})</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.freight_cost}
                    onChange={(e) => setForm({ ...form, freight_cost: parseFloat(e.target.value) || 0 })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Installation & Setup ({currency})</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.installation_cost}
                    onChange={(e) => setForm({ ...form, installation_cost: parseFloat(e.target.value) || 0 })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Inspection & Condition Status</label>
                  <select
                    value={form.inspection_status}
                    onChange={(e) => setForm({ ...form, inspection_status: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="Passed">Passed (100% Operational)</option>
                    <option value="Partial">Partial Delivery / Minor Blemish</option>
                    <option value="Failed">Failed Inspection (Damaged / Missing Parts)</option>
                  </select>
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Capitalization Decision</label>
                  <select
                    value={form.capitalization_decision}
                    onChange={(e) => setForm({ ...form, capitalization_decision: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="Capitalize">Capitalize as Fixed Asset</option>
                    <option value="Low-Value Expense">Track as Low-Value Asset (Expensed)</option>
                    <option value="Inventory">Direct Ingestion into Inventory</option>
                  </select>
                </div>
              </div>

              {/* Items Section */}
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3 bg-gray-50/50 dark:bg-neutral-900/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Delivered Assets & Equipment List
                  </span>
                  <button type="button" onClick={handleAddItemRow} className="btn btn-xs btn-outline">
                    + Add Item
                  </button>
                </div>

                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        required
                        placeholder="Equipment Name (e.g. Panasonic Inverter AC 2-Ton)"
                        value={item.name}
                        onChange={(e) => handleItemChange(idx, "name", e.target.value)}
                        className="input input-xs input-bordered flex-1"
                      />
                      <select
                        value={item.category_id}
                        onChange={(e) => handleItemChange(idx, "category_id", e.target.value)}
                        className="select select-xs select-bordered w-40"
                      >
                        <option value="">Category</option>
                        {(categoryList || []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(idx, "quantity", parseInt(e.target.value) || 1)}
                        className="input input-xs input-bordered w-16"
                        title="Quantity"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Cost"
                        value={item.unit_cost}
                        onChange={(e) => handleItemChange(idx, "unit_cost", parseFloat(e.target.value) || 0)}
                        className="input input-xs input-bordered w-24"
                        title="Unit Cost"
                      />
                      {form.items.length > 1 && (
                        <button type="button" onClick={() => handleRemoveItem(idx)} className="btn btn-xs btn-ghost text-red-500">
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="gen_assets"
                    checked={form.generate_assets}
                    onChange={(e) => setForm({ ...form, generate_assets: e.target.checked })}
                    className="checkbox checkbox-xs checkbox-primary"
                  />
                  <label htmlFor="gen_assets" className="text-xs font-semibold cursor-pointer text-gray-700 dark:text-gray-300">
                    Automatically generate individual asset records with sequential QR tags in Asset Register
                  </label>
                </div>
              </div>

              <div className="modal-action justify-between mt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="btn btn-sm btn-ghost">
                  Cancel
                </button>
                <button type="submit" className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white">
                  Save & Ingest Equipment
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
}
